import { useMemo, type ReactNode, type Ref } from "react";
import { AppWindow, Folder, RefreshCw, User } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  DataTable,
  TableIdentity,
  localTablePage,
  updateTableState,
  type DataTableChange,
  type DataTableColumn,
  type DataTableState,
  type SelectOption,
} from "@h2j-data-team/ui";
import type { QlikApp } from "@repo/shared";

import { DensityToggle } from "@/components/density-toggle";
import { EMPTY_APP_FILTERS, type AppFilters } from "./app-filters";
import { FacetFilter } from "./facet-filter";
import { useFormatDate } from "@/hooks/use-format-date";
import { paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";

const PAGE_SIZES = [10, 25, 50, 100];

function matchesFilters(app: QlikApp, f: AppFilters) {
  const matchesPublished =
    f.status.length === 0 ||
    (f.status.includes("published") && !!app.published) ||
    (f.status.includes("unpublished") && !app.published);
  const matchesSpace =
    f.spaces.length === 0 || (!!app.spaceName && f.spaces.includes(app.spaceName));
  const matchesOwner = f.owners.length === 0 || (!!app.ownerId && f.owners.includes(app.ownerId));
  const matchesAccess =
    f.access.length === 0 ||
    (f.access.includes("editable") && app.canUpdate !== false) ||
    (f.access.includes("readonly") && app.canUpdate === false);
  return matchesPublished && matchesSpace && matchesOwner && matchesAccess;
}

interface AppTableProps {
  /** Every app of the connection: the table filters, searches, sorts and pages them. */
  apps: QlikApp[];
  connectionId: string;
  /** Accessible name of the table. */
  label: string;
  state: DataTableState<AppFilters>;
  onStateChange: (state: DataTableState<AppFilters>, change: DataTableChange) => void;
  /** First load of the list. */
  loading?: boolean;
  /** Safe failure of the list (never the raw error message). */
  error?: { title: string } | null;
  onRetry?: () => void;
  /** Shown when the connection has no apps. */
  emptyAction?: ReactNode;
  /** Show the app's "Last updated" date column. */
  showDates?: boolean;
  /** Show the metadata-cache freshness column + per-row refresh action. */
  showCache?: boolean;
  cacheByAppId?: Map<string, string | null>;
  /** A metadata refresh is running: the per-row buttons wait. */
  refreshing?: boolean;
  /** The app whose refresh is running (its icon spins). */
  refreshingAppId?: string | null;
  onRefreshMetadata?: (appId: string) => void;
  /** Passing the bulk actions of the selected rows enables the leading checkbox column. */
  bulkActions?: (ids: readonly string[]) => ReactNode;
  searchRef?: Ref<HTMLInputElement>;
}

/** Tables-first browse of Qlik apps; the name opens the single-app editor. */
export function AppTable({
  apps,
  connectionId,
  label,
  state,
  onStateChange,
  loading,
  error,
  onRetry,
  emptyAction,
  showDates,
  showCache,
  cacheByAppId,
  refreshing,
  refreshingAppId,
  onRefreshMetadata,
  bulkActions,
  searchRef,
}: AppTableProps) {
  const { t } = useTranslation("single-app");
  const { t: tc } = useTranslation("connections");
  const { formatDate } = useFormatDate();
  const { density, setDensity } = useTableDensity();

  const lastColLabel = showCache
    ? t("appTable.lastCached")
    : showDates
      ? t("appTable.updated")
      : null;

  const columns = useMemo<DataTableColumn<QlikApp>[]>(() => {
    const cols: DataTableColumn<QlikApp>[] = [
      {
        id: "name",
        header: t("appTable.name"),
        variant: "identity",
        rowHeader: true,
        rowLink: true,
        sort: "text",
        value: (app) => app.name,
        cell: (app) => (
          <TableIdentity
            leading={<AppWindow />}
            detailMono={false}
            detail={app.description || undefined}
            name={
              <span className="flex min-w-0 items-center gap-2">
                <Link
                  to="/single-app/$connectionId/apps/$appId"
                  params={{ connectionId, appId: app.id }}
                  className="truncate font-medium text-ink hover:underline"
                >
                  {app.name}
                </Link>
                {app.canUpdate === false && (
                  <Badge variant="secondary" className="shrink-0">
                    {t("appCard.readOnly")}
                  </Badge>
                )}
              </span>
            }
          />
        ),
      },
      {
        id: "space",
        header: t("appTable.space"),
        width: "11rem",
        sort: "text",
        value: (app) => app.spaceName,
        cell: (app) =>
          app.spaceName ? (
            <span className="flex min-w-0 items-center gap-1.5 text-dim">
              <Folder className="h-3 w-3 shrink-0 text-faint" strokeWidth={1.8} aria-hidden />
              <span className="truncate">{app.spaceName}</span>
            </span>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        id: "owner",
        header: t("appTable.owner"),
        width: "11rem",
        sort: "text",
        value: (app) => app.ownerName,
        cell: (app) =>
          app.ownerName ? (
            <span className="flex min-w-0 items-center gap-1.5 text-dim">
              <User className="h-3 w-3 shrink-0 text-faint" strokeWidth={1.8} aria-hidden />
              <span className="truncate">{app.ownerName}</span>
            </span>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
    ];
    if (lastColLabel) {
      const dateOf = (app: QlikApp) =>
        (showCache ? cacheByAppId?.get(app.id) : app.updatedAt) ?? null;
      cols.push({
        id: "date",
        header: lastColLabel,
        // Fits "ÚLTIMA ATUALIZAÇÃO" with the sort icon (10rem cut the pt-BR header).
        width: "12.5rem",
        sort: "date",
        value: (app) => {
          const iso = dateOf(app);
          return iso ? new Date(iso) : null;
        },
        cell: (app) => {
          const iso = dateOf(app);
          return (
            <span className="font-mono text-xs text-dim">
              {iso ? formatDate(iso) : showCache ? t("appCard.noCache") : "—"}
            </span>
          );
        },
      });
    }
    return cols;
  }, [cacheByAppId, connectionId, formatDate, lastColLabel, showCache, t]);

  const page = localTablePage(apps, state, {
    columns,
    search: (app) => `${app.name} ${app.description ?? ""}`,
    filter: matchesFilters,
  });

  const spaceOptions = useMemo<SelectOption[]>(() => {
    const spaces = new Set<string>();
    for (const app of apps) if (app.spaceName) spaces.add(app.spaceName);
    return [...spaces].sort().map((space) => ({ value: space, label: space }));
  }, [apps]);

  const ownerOptions = useMemo<SelectOption[]>(() => {
    const owners = new Map<string, string>();
    for (const app of apps)
      if (app.ownerId && app.ownerName) owners.set(app.ownerId, app.ownerName);
    return [...owners.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [apps]);

  const statusOptions: SelectOption[] = [
    { value: "published", label: tc("apps.published") },
    { value: "unpublished", label: tc("apps.unpublished") },
  ];
  const accessOptions: SelectOption[] = [
    { value: "editable", label: tc("apps.editable") },
    { value: "readonly", label: tc("apps.readOnly") },
  ];

  const setFilter = (patch: Partial<AppFilters>) =>
    onStateChange(updateTableState(state, { filters: { ...state.filters, ...patch } }), {
      reason: "user",
    });

  const clearFilters = () =>
    onStateChange(updateTableState(state, { query: "", filters: EMPTY_APP_FILTERS }), {
      reason: "user",
    });

  return (
    <DataTable
      label={label}
      columns={columns}
      rows={page.rows}
      getRowId={(app) => app.id}
      state={state}
      onStateChange={onStateChange}
      density={density}
      loading={loading}
      error={error}
      onRetry={onRetry}
      search={{
        ...tableSearch(tc("apps.searchPlaceholder")),
        inputRef: searchRef,
        shortcutHint: "/",
        debounceMs: 300,
      }}
      toolbar={
        <>
          <FacetFilter
            label={t("appTable.space")}
            options={spaceOptions}
            value={state.filters.spaces}
            onValueChange={(spaces) => setFilter({ spaces })}
          />
          <FacetFilter
            label={t("appTable.owner")}
            options={ownerOptions}
            value={state.filters.owners}
            onValueChange={(owners) => setFilter({ owners })}
          />
          <FacetFilter
            label={t("appTable.status")}
            options={statusOptions}
            value={state.filters.status}
            onValueChange={(status) => setFilter({ status })}
          />
          <FacetFilter
            label={t("appTable.access")}
            options={accessOptions}
            value={state.filters.access}
            onValueChange={(access) => setFilter({ access })}
          />
          <DensityToggle value={density} onChange={setDensity} />
        </>
      }
      pagination={{
        kind: "offset",
        total: page.total,
        pageSizeOptions: PAGE_SIZES,
        labels: paginationLabels(),
      }}
      selection={
        bulkActions
          ? {
              mode: "across-pages",
              getRowLabel: (app) => app.name,
              actions: bulkActions,
              labels: {
                selectPage: t("appTable.selectPage"),
                selectRow: (name) => t("appTable.selectRow", { name }),
                selected: (count) => t("apps.bulk.selected", { count }),
                clear: t("apps.bulk.clear"),
              },
            }
          : undefined
      }
      rowActions={
        onRefreshMetadata
          ? {
              label: t("appTable.actions"),
              width: 56,
              render: (app) => (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={refreshing}
                  aria-label={t("appTable.refreshRow", { name: app.name })}
                  title={t("appCard.refreshMetadata")}
                  onClick={() => onRefreshMetadata(app.id)}
                >
                  <RefreshCw className={refreshingAppId === app.id ? "h2j-spin" : undefined} />
                </Button>
              ),
            }
          : undefined
      }
      messages={tableMessages({
        empty: {
          title: tc("apps.empty.title"),
          description: tc("apps.empty.description"),
          action: emptyAction,
        },
        noResults: {
          title: tc("apps.noMatch.title"),
          description: tc("apps.noMatch.description"),
          action: (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {tc("apps.noMatch.clearFilters")}
            </Button>
          ),
        },
      })}
    />
  );
}
